// Auftragsbestätigungen und Lieferscheine ohne Oberfläche: Antworten des Servers (server/src/modules/rechnungstool/
// auftraege.ts), Adressen und Texte der Liste. Benutzt von Auftraege.tsx, AuftragForm.tsx, LieferscheinForm.tsx,
// Angebote.tsx und RechnungForm.tsx (Rechnung aus AB bzw. Lieferscheinen).
// Eine Zeile der Liste wie bei den Rechnungen (RechnungZeile.ts): höchstens eine graue Zeile (auftragInfo), ein Knopf für
// den nächsten Schritt (auftragSchritt) und das Menü „···“ mit allem anderen (auftragMenue). Nur Daten, kein React und
// kein Tarif: die Rechte kommen im Kontext. Keine E-Mail-Adressen in der Liste (die stehen im Fenster „Per E-Mail“).
import { kurzTag } from '../lib/format';
import type { EFormat, Kunde, Position, RtArt, Versand } from './types';
import type { Waehrung } from './waehrung';

export type AuftragArt = 'auftrag' | 'lieferschein';
export type AuftragStatus = 'offen' | 'storniert';
// Stand in der Liste (auftragstand.ts): AB offen, teilweise geliefert, geliefert, berechnet; LS offen, berechnet oder
// zur Rechnung (aus einer Rechnung oder einem berechneten Auftrag); beide storniert
export type AuftragAnzeige = 'offen' | 'teilweise' | 'geliefert' | 'berechnet' | 'zur_rechnung' | 'storniert';

export interface Ref {
  id: string;
  number: string;
  status: string;
}
// Angebot → Auftrag → Lieferscheine → Rechnungen (stornierte Rechnungen auch, die Liste zeigt sie durchgestrichen)
export interface Vorgang {
  angebot: Ref | null;
  auftrag: Ref | null;
  lieferscheine: Ref[];
  rechnungen: Ref[];
}

export interface AuftragItem {
  id: string;
  art: AuftragArt;
  number: string;
  date: string;
  // AB: Liefertermin (frei), LS: Lieferdatum
  lieferDate: string | null;
  customer: string;
  zip: string;
  kundeId: string | null;
  site: string | null;
  bestellnummer: string | null;
  // LS: Preise auf dem Lieferschein (die AB immer)
  mitPreisen: boolean;
  netto: number;
  betrag: number;
  // Beträge in dieser Währung; Kurs (Euro je Einheit) für die Summen in Euro
  waehrung: Waehrung;
  kurs: number;
  status: AuftragStatus;
  statusAt: string | null;
  anzeige: AuftragAnzeige;
  // daraus darf jetzt eine Rechnung geschrieben werden
  berechenbar: boolean;
  // Rechnung daraus oder (LS) die Rechnung, zu der er gehört
  rechnung: Ref | null;
  vorgang: Vorgang;
  versand: Versand[];
}

export interface AuftragListe {
  heute: string;
  items: AuftragItem[];
  kennzahlen: { offen: number; zuBerechnen: { anzahl: number; betrag: number }; lieferungenMonat: number };
}

// Ein Beleg mit allem zum Kopieren (GET /auftrag/:id bzw. /lieferschein/:id)
export type AuftragVoll = Omit<AuftragItem, 'customer'> & {
  customer: Kunde;
  items: Position[];
  steuerHinweis: string | null;
  intro: string | null;
  note: string | null;
  kleinunternehmer: boolean;
  ust: number;
};

// Formular der AB vorbefüllt (GET …/auftraege/vorlage?von=angebot|auftrag)
export interface AuftragVorlage {
  angebot: { id: string; number: string; date: string; validUntil: string; anzeige: string } | null;
  // Kopie dieser AB
  kopie: { number: string } | null;
  warnung: string | null;
  form: {
    angebotId: string | null;
    kundeId: string | null;
    customer: Kunde;
    items: Position[];
    site: string | null;
    intro: string | null;
    note: string | null;
    bestellnummer: string | null;
    steuerHinweis: string | null;
    liefertermin: string | null;
    // Währung und Kurs der Quelle (Kopie: Kurs null, neu vorgeschlagen)
    waehrung: Waehrung;
    kurs: number | null;
  };
}

export type LieferscheinQuelle = 'auftrag' | 'angebot' | 'rechnung';
// Formular des LS vorbefüllt (GET …/lieferscheine/vorlage?von=auftrag|angebot|rechnung|lieferschein): Positionen mit den
// Preisen der Quelle (Kopie eines LS ohne Preise: null), Lieferdatum heute
export interface LieferscheinVorlage {
  quelle: { art: LieferscheinQuelle; id: string; number: string } | null;
  // Kopie dieses LS
  kopie: { number: string } | null;
  warnung: string | null;
  form: {
    kundeId: string | null;
    customer: Kunde;
    items: (Omit<Position, 'price'> & { price: number | null })[];
    site: string | null;
    note: string | null;
    bestellnummer: string | null;
    steuerHinweis: string | null;
    lieferdatum: string;
    mitPreisen: boolean;
    quelle: { art: LieferscheinQuelle; id: string } | null;
    // Währung und Kurs der Quelle (Kopie: Kurs null, neu vorgeschlagen)
    waehrung: Waehrung;
    kurs: number | null;
  };
}

// Rechnungsformular aus AB bzw. LS (GET /auftrag/:id/rechnung, …/lieferscheine/rechnung?ids=); Preise aus LS ohne
// Preise: null (leer im Formular)
export interface AuftragRechnung {
  // Nummer und Datum der Belege (Kopfzeile im Formular)
  aus: { number: string; date: string }[];
  warnung: string | null;
  form: {
    auftragId: string | null;
    lieferscheinIds: string[];
    kundeId: string | null;
    customer: Kunde;
    eFormat: EFormat;
    art: RtArt;
    items: (Omit<Position, 'price'> & { price: number | null })[];
    steuerHinweis: string | null;
    serviceFrom: string | null;
    serviceTo: string | null;
    site: string | null;
    paymentDays: number;
    note: string;
    // Währung der Belege (den Kurs schlägt das Formular zum Rechnungsdatum neu vor)
    waehrung: Waehrung;
  };
}

export const auftraegeUrl = (firmId: string) => `/api/rechnungstool/${firmId}/auftraege`;
export const lieferscheineUrl = (firmId: string) => `/api/rechnungstool/${firmId}/lieferscheine`;
export const auftragUrl = (art: AuftragArt, id: string) => `/api/rechnungstool/${art}/${id}`;
export const auftragPdfUrl = (art: AuftragArt, id: string) => `${auftragUrl(art, id)}/pdf`;
export const auftragVorlageUrl = (firmId: string, von: 'angebot' | 'auftrag', id: string) => `${auftraegeUrl(firmId)}/vorlage?von=${von}&id=${id}`;
export const lieferscheinVorlageUrl = (firmId: string, von: LieferscheinQuelle | 'lieferschein', id: string) =>
  `${lieferscheineUrl(firmId)}/vorlage?von=${von}&id=${id}`;
export const rechnungAusAuftragUrl = (id: string) => `${auftragUrl('auftrag', id)}/rechnung`;
export const rechnungAusLieferscheinenUrl = (firmId: string, ids: string[]) => `${lieferscheineUrl(firmId)}/rechnung?ids=${ids.join(',')}`;
// „Auftrag bestätigen“ beim Angebot (ein Klick)
export const auftragAusAngebotUrl = (angebotId: string) => `/api/rechnungstool/angebot/${angebotId}/auftrag`;

// Formulare im Browser (RechnungForm.tsx liest ?auftrag= und ?lieferscheine=)
export const rechnungAusAuftrag = (id: string) => `/rechnung/neu?auftrag=${id}`;
export const rechnungAusLieferscheinen = (ids: string[]) => `/rechnung/neu?lieferscheine=${ids.join(',')}`;
export const neuerAuftrag = (von?: { angebot?: string; vorlage?: string }) =>
  `/rechnung/auftraege/neu${von?.angebot ? `?angebot=${von.angebot}` : von?.vorlage ? `?vorlage=${von.vorlage}` : ''}`;
export const neuerLieferschein = (von?: { art: LieferscheinQuelle | 'vorlage'; id: string }) =>
  `/rechnung/auftraege/lieferschein${von ? `?${von.art}=${von.id}` : ''}`;

export const ART_NAME: Record<AuftragArt, string> = { auftrag: 'Auftragsbestätigung', lieferschein: 'Lieferschein' };
// Chip in der Zeile
export const ART_KURZ: Record<AuftragArt, string> = { auftrag: 'Auftrag', lieferschein: 'Lieferschein' };

// Status-Chip: Text und Farbe wie auf jeder Seite (offen und wartend gelb, erledigt grün, nichts mehr zu tun grau); ohne
// Nummer, die Rechnung steht in der grauen Zeile
export function statusOf(x: AuftragItem): { text: string; cls: string } {
  switch (x.anzeige) {
    case 'offen':
      return { text: 'Offen', cls: 'b-warn' };
    case 'teilweise':
      return { text: 'Teilweise geliefert', cls: 'b-warn' };
    case 'geliefert':
      return { text: 'Geliefert', cls: 'b-none' };
    case 'berechnet':
      return { text: 'Berechnet', cls: 'b-ok' };
    case 'zur_rechnung':
      return { text: 'Zur Rechnung', cls: 'b-ok' };
    case 'storniert':
      return { text: 'Storniert', cls: 'b-none' };
  }
}

// Vorgang als Text (Kette der Nummern), z. B. für die Suche: „Angebot AN-… → Auftrag AB-… → Lieferscheine LS-…, LS-… →
// Rechnung RE-…“. Die Liste hat dieselben Belege als Sprünge im Menü „···“ (auftragMenue).
export function vorgangTeile(v: Vorgang): { label: string; refs: Ref[] }[] {
  const teile: { label: string; refs: Ref[] }[] = [];
  if (v.angebot) teile.push({ label: 'Angebot', refs: [v.angebot] });
  if (v.auftrag) teile.push({ label: 'Auftrag', refs: [v.auftrag] });
  if (v.lieferscheine.length) teile.push({ label: v.lieferscheine.length === 1 ? 'Lieferschein' : 'Lieferscheine', refs: v.lieferscheine });
  if (v.rechnungen.length) teile.push({ label: v.rechnungen.length === 1 ? 'Rechnung' : 'Rechnungen', refs: v.rechnungen });
  return teile;
}
export const vorgangText = (v: Vorgang) => vorgangTeile(v).map((t) => `${t.label} ${t.refs.map((r) => r.number).join(', ')}`).join(' → ');

// Rechte des Logins (Billtoro: darf, in der Kanzlei-App alles): „Angebote und Aufträge“ bzw. „Rechnungen“
export interface AuftragKontext {
  bieten: boolean;
  rechnen: boolean;
}

// Knopf oder Eintrag, der zu einem Formular bzw. einer Liste führt
export interface AuftragLink {
  text: string;
  ziel: string;
}
// Eintrag im Menü „···“: per E-Mail und Stornieren öffnen ein Fenster, alles andere ist ein Link
export type AuftragEintrag = { art: 'mail' | 'storno'; text: string } | ({ art: 'link' } & AuftragLink) | { art: 'trenner' };

// Stornieren geht ohne gültige Rechnung daraus; eine AB nur ohne offene Lieferscheine
export const stornierbar = (x: AuftragItem) =>
  x.status === 'offen' && x.anzeige !== 'berechnet' && (x.art === 'lieferschein' || !x.vorgang.lieferscheine.some((l) => l.status === 'offen'));

const rechnungAus = (x: AuftragItem) => (x.art === 'auftrag' ? rechnungAusAuftrag(x.id) : rechnungAusLieferscheinen([x.id]));

// Der eine Knopf der Zeile (erste passende Regel): Rechnung aus der AB bzw. dem LS, sonst der Lieferschein zur offenen
// AB. Ohne das Recht kein Knopf.
export function auftragSchritt(x: AuftragItem, ctx: AuftragKontext): AuftragLink | null {
  if (x.status === 'storniert') return null;
  if (x.berechenbar && ctx.rechnen) return { text: 'Rechnung schreiben', ziel: rechnungAus(x) };
  if (x.art === 'auftrag' && (x.anzeige === 'offen' || x.anzeige === 'teilweise') && ctx.bieten)
    return { text: 'Lieferschein', ziel: neuerLieferschein({ art: 'auftrag', id: x.id }) };
  return null;
}

// Graue Zeile unter dem Kunden: die ersten zwei passenden Angaben, Tage kurz (kurzTag). Das Lieferdatum des LS nur,
// wenn es nicht das Datum des Belegs ist (das steht schon in der Zeile). Die Rechnung: aus dem Beleg selbst, bei einer
// AB ohne eigene Rechnung die gültigen Rechnungen ihres Vorgangs (über die Lieferscheine berechnet). null: keine Zeile.
export function auftragInfo(x: AuftragItem, today: string): string | null {
  const ab = x.art === 'auftrag';
  const rechnungen = x.rechnung ? [x.rechnung] : ab ? x.vorgang.rechnungen.filter((r) => r.status !== 'storniert') : [];
  const teile = [
    x.status === 'storniert' && x.statusAt ? `storniert am ${kurzTag(x.statusAt, today)}` : null,
    x.lieferDate && (ab || x.lieferDate !== x.date) ? `${ab ? 'Liefertermin' : 'Geliefert'} ${kurzTag(x.lieferDate, today)}` : null,
    rechnungen.length ? `${rechnungen.length === 1 ? 'Rechnung' : 'Rechnungen'} ${rechnungen.map((r) => r.number).join(', ')}` : null,
    x.site ? `${ab ? 'Einsatzort' : 'Lieferort'} ${x.site}` : null,
    x.bestellnummer ? `Bestellnummer ${x.bestellnummer}` : null,
  ].filter((t): t is string => !!t);
  return teile.length ? teile.slice(0, 2).join(' · ') : null;
}

// Sprünge zu den anderen Belegen des Vorgangs (der Beleg selbst nicht), in der Reihenfolge Angebot → Auftrag →
// Lieferscheine → Rechnungen; die Liste dort sucht nach der Nummer
function spruenge(x: AuftragItem): AuftragLink[] {
  const v = x.vorgang;
  const suche = (seite: string, r: Ref) => `/rechnung/${seite}?suche=${encodeURIComponent(r.number)}`;
  const andere = (r: Ref | null): r is Ref => !!r && r.id !== x.id;
  return [
    ...[v.angebot].filter(andere).map((r) => ({ text: `Zum Angebot ${r.number}`, ziel: suche('angebote', r) })),
    ...[v.auftrag].filter(andere).map((r) => ({ text: `Zum Auftrag ${r.number}`, ziel: suche('auftraege', r) })),
    ...v.lieferscheine.filter(andere).map((r) => ({ text: `Zum Lieferschein ${r.number}`, ziel: suche('auftraege', r) })),
    ...v.rechnungen.map((r) => ({ text: `Zur Rechnung ${r.number}${r.status === 'storniert' ? ' (storniert)' : ''}`, ziel: suche('rechnungen', r) })),
  ];
}

// Menü „···“: alles, was nicht der Knopf ist (Bedingungen wie früher in der Zeile; die PDF ist der Klick auf die Zeile).
// Reihenfolge: verschicken, schreiben, kopieren | Sprünge im Vorgang | Stornieren. Ein leeres Menü zeigt die Zeile nicht.
export function auftragMenue(x: AuftragItem, ctx: AuftragKontext, schritt: AuftragLink | null): AuftragEintrag[] {
  const e: AuftragEintrag[] = [];
  const link = (text: string, ziel: string) => {
    if (schritt?.text !== text) e.push({ art: 'link', text, ziel });
  };
  const trenner = () => {
    if (e.length) e.push({ art: 'trenner' });
  };
  if (x.status !== 'storniert' && ctx.bieten) e.push({ art: 'mail', text: 'Per E-Mail' });
  if (x.art === 'auftrag' && x.status === 'offen' && ctx.bieten) link('Lieferschein', neuerLieferschein({ art: 'auftrag', id: x.id }));
  if (x.berechenbar && ctx.rechnen) link('Rechnung schreiben', rechnungAus(x));
  if (ctx.bieten) link('Kopieren', x.art === 'auftrag' ? neuerAuftrag({ vorlage: x.id }) : neuerLieferschein({ art: 'vorlage', id: x.id }));
  const s = spruenge(x);
  if (s.length) {
    trenner();
    for (const l of s) e.push({ art: 'link', ...l });
  }
  if (stornierbar(x) && ctx.bieten) {
    trenner();
    e.push({ art: 'storno', text: 'Stornieren' });
  }
  return e;
}
